import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import NotificationHistoryCard from '../../components/NotificationHistoryCard';
import { HostSettingsLayout } from '../../components/HostSettingsLayout';
import RoleNotificationPrefsCard from '../../components/RoleNotificationPrefsCard';
import { fetchAccountSettings, fetchNotificationHistory, updateAccountNotifications } from '../../utils/accountSettingsApi';

/**
 * Notification settings page shown to venue staff members at
 * `/staff-app/account-settings/notifications` (regular staff) and
 * `/staff-admin/account-settings/notifications` (admin staff).
 *
 * The visible toggles differ by `staff_role`:
 * - admin staff (`staff_role === 'admin'`) sees Direct messages + Ratings/reviews.
 * - regular staff sees Direct messages only.
 *
 * Platform admin (`role === 'admin'` on the user record) is a separate
 * audience and is intentionally NOT served by this page.
 */
export default function AdminStaffNotificationsPage() {
  const [notifications, setNotifications] = useState({});
  const [prefsConfig, setPrefsConfig] = useState({});
  const [history, setHistory] = useState([]);

  const staffRoleKey = useMemo(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem('user') : null;
      if (!raw) return 'venue_staff';
      const u = JSON.parse(raw) || {};
      return (u.staff_role || '').toLowerCase() === 'admin' ? 'venue_admin' : 'venue_staff';
    } catch (e) {
      return 'venue_staff';
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [settings, historyItems] = await Promise.all([
          fetchAccountSettings(),
          fetchNotificationHistory({ scope: 'account' }),
        ]);
        setNotifications(settings.notifications || {});
        setPrefsConfig(settings.notification_prefs_config || {});
        setHistory(historyItems || []);
      } catch (err) {
        toast.error('Failed to load notification settings');
      }
    };
    load();
  }, []);

  const persist = async (next, previous) => {
    setNotifications(next);
    try {
      await updateAccountNotifications(next);
    } catch (err) {
      setNotifications(previous);
      toast.error(err.response?.data?.detail || 'Failed to update notifications');
    }
  };

  const handleToggle = (key) => {
    const current = notifications[key] !== false;
    const next = { ...notifications, [key]: !current };
    persist(next, notifications);
  };

  const testIdPrefix = staffRoleKey === 'venue_admin' ? 'venue-admin-notifications' : 'staff-notifications';

  return (
    <HostSettingsLayout title="Notifications & History" dataTestId={`${testIdPrefix}-page`}>
      <RoleNotificationPrefsCard
        role={staffRoleKey}
        config={prefsConfig}
        notifications={notifications}
        onToggle={handleToggle}
        testIdPrefix={`${testIdPrefix}-prefs`}
      />

      <NotificationHistoryCard notifications={history} dataTestIdPrefix={`${testIdPrefix}-history`} />
    </HostSettingsLayout>
  );
}
